import {
  DataType,
  type TableData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../../DataProvider/types';
import {
  getTableDimensionValue,
  getTableValue,
  groupTableRows,
} from '../../DataProvider/utils/table-data';
import type { SubfilterTree } from '../../PageState/types';

export const getSegmentLeafId = (id: string | undefined): string | null => {
  if (!id) return null;
  return id.split('_').slice(-1)[0] ?? null;
};

export const normalizeSegmentName = (
  name: string | null | undefined
): string | null => {
  const normalized = name?.trim().toLowerCase().replace(/\s+/g, ' ');
  return normalized ? normalized : null;
};

export const getTableSegments = (table: TableData): SubfilterTree[] => {
  const [, segment] = table.dimensions;
  const column =
    segment === undefined ? undefined : table.dimensionColumns[segment];
  return (column?.values ?? []).map(({ id, label }) => ({ id, name: label }));
};

type SharePoint = TimeseriesDataPoint & {
  value: number;
  secondaryValue: number;
};

export const tableToTimeSeriesData = (
  table: TableData,
  selectedSegments: readonly SubfilterTree[]
): TimeseriesDataSet | null => {
  const [entity, segment, month] = table.dimensions;
  if (
    entity === undefined ||
    segment === undefined ||
    month !== 'timeseries' ||
    selectedSegments.length === 0
  ) {
    return null;
  }

  const ids = new Set(
    selectedSegments
      .map((selected) => getSegmentLeafId(selected?.id))
      .filter((id): id is string => id !== null)
  );
  const names = new Set(
    selectedSegments
      .map((selected) => normalizeSegmentName(selected?.name))
      .filter((name): name is string => name !== null)
  );
  const isSelected = (row: number): boolean => {
    const value = getTableDimensionValue(table, segment, row);
    if (value === undefined) return false;
    const name = normalizeSegmentName(value.label);
    return ids.has(value.id) || (name !== null && names.has(name));
  };

  return {
    type: DataType.TIMESERIES,
    data: groupTableRows(table, entity).map(({ value, rows }) => {
      const points = new Map<string, SharePoint>();
      for (const row of rows) {
        const date = getTableDimensionValue(table, month, row)?.label;
        if (date === undefined || !isSelected(row)) continue;
        const point = points.get(date) ?? {
          date,
          value: 0,
          secondaryValue: 0,
        };
        point.value += getTableValue(table, 'share_of_headcount', row) ?? 0;
        point.secondaryValue += getTableValue(table, 'headcount', row) ?? 0;
        points.set(date, point);
      }
      return {
        dim: value.label,
        timeseries: Array.from(points.values(), (point) => ({
          ...point,
          value: Math.round(point.value * 10000) / 10000,
        })),
      };
    }),
  };
};
